import React, { useState } from 'react';
import { AlertCircle, AlertTriangle, Clock, Bug, Send } from 'lucide-react';
import FileUploadField from './FileUploadField';
import { api, ApiError } from '../../lib/api';

const inputClasses =
  'w-full px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 focus:bg-white/[0.05] transition-all';
const labelClasses = 'block text-[11px] font-medium text-gray-400/80 mb-1.5 tracking-wide';

const PRIORITIES = [
  { value: 'low', label: 'Low', desc: 'Nice to have', icon: Clock, color: 'text-green-400', ring: 'border-green-400/40 bg-green-400/[0.06]' },
  { value: 'medium', label: 'Medium', desc: 'Important', icon: AlertCircle, color: 'text-yellow-400', ring: 'border-yellow-400/40 bg-yellow-400/[0.06]' },
  { value: 'high', label: 'High', desc: 'Needs attention', icon: AlertTriangle, color: 'text-orange-400', ring: 'border-orange-400/40 bg-orange-400/[0.06]' },
  { value: 'critical', label: 'Critical', desc: 'Blocking', icon: Bug, color: 'text-red-400', ring: 'border-red-400/40 bg-red-400/[0.06]' },
];

const EMPTY = {
  title: '',
  description: '',
  priority: 'medium',
  affectedUrl: '',
  attachments: [],
};

export default function TicketForm({ onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [fieldError, setFieldError] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldError((prev) => ({ ...prev, [field]: undefined }));
    setGeneralError('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setFieldError({});
    setGeneralError('');

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      priority: form.priority,
      affectedUrl: form.affectedUrl.trim() || null,
      attachments: form.attachments,
    };

    try {
      const data = await api.post('/api/tickets', payload);
      setForm(EMPTY);
      onCreated?.(data.ticket);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.data?.field) {
          setFieldError({ [err.data.field]: err.message });
        } else {
          setGeneralError(err.message || 'Failed to submit ticket');
        }
      } else {
        setGeneralError('Network error, try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5 p-6 md:p-7 rounded-2xl bg-white/[0.025] border border-white/[0.06]" noValidate>
      <div>
        <h3 className="text-sm font-medium text-white tracking-tight">New support ticket</h3>
        <p className="text-[11px] text-gray-500 mt-0.5">
          Describe the issue. NexSav gets notified by email the moment you submit.
        </p>
      </div>

      {generalError && (
        <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
          {generalError}
        </div>
      )}

      <div>
        <label className={labelClasses} htmlFor="ticket-title">Title *</label>
        <input
          id="ticket-title"
          type="text"
          value={form.title}
          onChange={onChange('title')}
          className={inputClasses}
          placeholder="Short summary, e.g. Calendar page missing October events"
          maxLength={200}
          required
        />
        {fieldError.title && <p className="mt-1 text-[11px] text-red-400">{fieldError.title}</p>}
      </div>

      <div>
        <label className={labelClasses} htmlFor="ticket-description">Description *</label>
        <textarea
          id="ticket-description"
          value={form.description}
          onChange={onChange('description')}
          rows={5}
          className={`${inputClasses} resize-y min-h-[120px]`}
          placeholder="What happened? What did you expect? Any screenshots help below."
          required
        />
        {fieldError.description && <p className="mt-1 text-[11px] text-red-400">{fieldError.description}</p>}
      </div>

      <div>
        <label className={labelClasses}>Priority *</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {PRIORITIES.map((p) => {
            const selected = form.priority === p.value;
            const Icon = p.icon;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => onChange('priority')(p.value)}
                className={`text-left p-3 rounded-lg border transition-all ${
                  selected
                    ? p.ring
                    : 'bg-white/[0.02] border-white/[0.06] hover:border-white/[0.12]'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`w-3.5 h-3.5 ${p.color}`} />
                  <span className={`text-[13px] font-medium ${selected ? 'text-white' : 'text-gray-300'}`}>
                    {p.label}
                  </span>
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">{p.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className={labelClasses} htmlFor="ticket-url">Affected URL</label>
        <input
          id="ticket-url"
          type="url"
          value={form.affectedUrl}
          onChange={onChange('affectedUrl')}
          className={inputClasses}
          placeholder="https://your-site.org/page-with-problem"
        />
      </div>

      <div>
        <label className={labelClasses}>Attachments</label>
        <FileUploadField
          attachments={form.attachments}
          onChange={(next) => onChange('attachments')(next)}
          disabled={submitting}
        />
      </div>

      <div className="flex items-center justify-end pt-2">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 hover:shadow-[#B0FC38]/30 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <span className="w-3.5 h-3.5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
              Submitting…
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Submit ticket
            </>
          )}
        </button>
      </div>
    </form>
  );
}
